import cn from 'classnames';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { auctionApi, AuctionBidDetails } from 'src/api/auctionApi';
import { CircleBrandingScope, useBranding } from 'src/branding';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { useCircleRoleView } from 'src/hooks/CircleRoleView/useCircleRoleView';
import {
  fetchAuctionIfLeader,
  fetchCircleBase,
  fetchCircleMessages,
} from 'src/redux/slices/circleDetailsSlice/circleDetailsSlice';
import { clearPosts, fetchPostsById } from 'src/redux/slices/feedSlice';
import TabsAuction from './Tabs/Tabs';
import styles from './UserCircle.module.scss';
import { UserInfo } from './UserInfo/UserInfo';

export const UserCircle = () => {
  const [auctionBidsInfo, setAuctionBidsInfo] =
    useState<AuctionBidDetails | null>(null);

  const dispatch = useAppDispatch();

  const { loading, circleData, auctionId, auctionData, videoMessages, error } =
    useAppSelector((state) => state.circleDetail);

  const { id } = useParams<{ id: string }>();
  const { actualIsLeader } = useCircleRoleView(id);
  const branding = useBranding(id);

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(clearPosts());

    const basePromise = dispatch(fetchCircleBase(id));

    basePromise.then((result) => {
      if (fetchCircleBase.fulfilled.match(result)) {
        dispatch(fetchPostsById({ id, page: 1 }));
      }
    });

    return () => {
      basePromise.abort();
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (!id || !circleData?._id) {
      return;
    }

    dispatch(fetchCircleMessages({ circleId: id, isLeader: actualIsLeader }));
  }, [id, circleData?._id, actualIsLeader, dispatch]);

  useEffect(() => {
    if (!id || !circleData?._id || !actualIsLeader) {
      return;
    }

    dispatch(fetchAuctionIfLeader(id));
  }, [id, circleData?._id, actualIsLeader, dispatch]);

  useEffect(() => {
    if (auctionId) {
      auctionApi.getSpecificAuction(auctionId).then((response) => {
        setAuctionBidsInfo(response.bidDetails);
      });
    }
  }, [auctionId]);

  const isWaitingForCircle =
    Boolean(id) && (loading || (!circleData && !error));

  return (
    <CircleBrandingScope branding={branding}>
      <div
        className={cn(styles.container, {
          [styles.brandedPage]: branding.hasPageSurface,
          [styles.brandedHeader]: branding.hasHeaderSurface,
        })}
      >
        {isWaitingForCircle ? (
          <div className={styles.loadingState}>
            <MaterialCircularProgress />
            <p>Loading circle...</p>
          </div>
        ) : id && circleData ? (
          <>
            <UserInfo
              {...circleData}
              auctionId={auctionId}
              auctionData={auctionData}
              auctionBidsInfo={auctionBidsInfo}
            />
            <TabsAuction
              circleId={id}
              circleName={circleData.tribe_name}
              videoMessages={videoMessages}
            />
          </>
        ) : (
          <p>Error loading circle data. Please try again.</p>
        )}
      </div>
    </CircleBrandingScope>
  );
};
